import { Link, createFileRoute, notFound, useParams } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { getImportedEventBySlug } from "@/lib/events/events.functions";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";

function eventQueryOptions(slug: string, fetcher: ReturnType<typeof useServerFn<typeof getImportedEventBySlug>> | null) {
  return queryOptions({
    queryKey: ["imported-event", slug],
    queryFn: async () => {
      // Use server fn directly during SSR/loader, hook in component
      if (fetcher) return await fetcher({ data: { slug } });
      return await getImportedEventBySlug({ data: { slug } });
    },
    staleTime: 5 * 60_000,
  });
}

export const Route = createFileRoute("/happening/$slug")({
  loader: async ({ params, context }) => {
    const { event } = await context.queryClient.ensureQueryData(
      eventQueryOptions(params.slug, null),
    );
    if (!event) throw notFound();
    return { slug: params.slug, event };
  },
  head: ({ loaderData }) => {
    const slug = loaderData?.slug ?? "";
    const event = loaderData?.event;
    const url = `https://whatfinland.com/happening/${slug}`;
    const title = event?.title
      ? `${event.title} — what Finland`
      : (slug ? `${slug.replace(/-/g, " ")} — what Finland` : "Live event — what Finland");
    const desc =
      event?.short_description ||
      event?.full_description?.slice(0, 200) ||
      "Live festival & event details, synced from external sources.";
    const image = event?.event_image || undefined;

    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "event" },
      { property: "og:url", content: url },
      ...buildLocaleMeta("en"),
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:card", content: "summary_large_image" });
      meta.push({ name: "twitter:image", content: image });
    }

    const scripts: Array<{ type: string; children: string }> = [];
    if (event) {
      // Normalize country to ISO 3166-1 alpha-2 (Google Rich Results requires this).
      const rawCountry = (event.country || "FI").trim();
      const countryMap: Record<string, string> = {
        finland: "FI", suomi: "FI", fi: "FI",
        sweden: "SE", se: "SE",
        norway: "NO", no: "NO",
        denmark: "DK", dk: "DK",
        estonia: "EE", ee: "EE",
      };
      const addressCountry = rawCountry.length === 2
        ? rawCountry.toUpperCase()
        : (countryMap[rawCountry.toLowerCase()] ?? "FI");

      // Only emit price when it parses as a non-negative number. Strings like
      // "Not specified" / "TBA" fail Google's Event rich-result validation.
      const rawPrice = (event as any).price_from ?? (event as any).price;
      const priceNum = typeof rawPrice === "number"
        ? rawPrice
        : (typeof rawPrice === "string" ? parseFloat(rawPrice.replace(/[^\d.,-]/g, "").replace(",", ".")) : NaN);
      const validPrice = Number.isFinite(priceNum) && priceNum >= 0;

      const eventLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Event",
        "@id": url,
        url,
        name: event.title,
        description: desc,
        ...(image && { image: [image] }),
        ...(event.start_date && { startDate: event.start_date }),
        ...(event.end_date && { endDate: event.end_date }),
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: event.venue || event.city || "Finland",
          address: {
            "@type": "PostalAddress",
            addressLocality: event.city || "Helsinki",
            addressCountry,
          },
          ...(event.lat != null && event.lng != null && {
            geo: { "@type": "GeoCoordinates", latitude: event.lat, longitude: event.lng },
          }),
        },
        ...(event.ticket_url && {
          offers: {
            "@type": "Offer",
            url: event.ticket_url,
            ...(validPrice && { price: String(priceNum), priceCurrency: "EUR" }),
            availability: "https://schema.org/InStock",
          },
        }),
        ...(event.organizer && {
          organizer: { "@type": "Organization", name: event.organizer },
        }),
        ...(event.source_url && { isBasedOn: event.source_url }),
        ...((event as any).updated_at && { dateModified: (event as any).updated_at }),
      };

      scripts.push({ type: "application/ld+json", children: JSON.stringify(eventLd) });

      const breadcrumbs = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://whatfinland.com/" },
          { "@type": "ListItem", position: 2, name: "Happening", item: "https://whatfinland.com/happening" },
          { "@type": "ListItem", position: 3, name: event.title, item: url },
        ],
      };
      scripts.push({ type: "application/ld+json", children: JSON.stringify(breadcrumbs) });
    }

    return {
      meta,
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks(`/happening/${slug}`)],
      scripts,
    };
  },
  component: EventDetailPage,
});

function formatDate(iso: string | null): string {
  if (!iso) return "Date TBA";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date TBA";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function EventDetailPage() {
  // `strict: false` so the /fi mirror route can reuse this component.
  const { slug } = useParams({ strict: false }) as { slug: string };
  const fetcher = useServerFn(getImportedEventBySlug);
  const { data } = useSuspenseQuery(eventQueryOptions(slug, fetcher));
  const event = data.event;
  if (!event) return null;

  return (
    <article className="min-h-screen pb-24 md:pb-0">
      {/* HERO */}
      <div className="relative h-[60vh] min-h-[420px] sm:h-[55vh] md:h-[50vh] md:min-h-[360px] w-full overflow-hidden bg-muted">
        {event.event_image && (
          <img
            src={event.event_image}
            alt={event.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 md:via-background/40 to-transparent" />
        <Link
          to="/happening"
          aria-label="Back to events"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 px-3 py-2 sm:px-4 rounded-full bg-background/85 backdrop-blur text-xs sm:text-sm font-semibold border border-border/40 hover:bg-background shadow-sm"
        >
          <ArrowLeft className="size-4" />
          Back
        </Link>
        <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-background/85 backdrop-blur text-[10px] sm:text-[11px] font-semibold mb-2.5 sm:mb-3 border border-border/40">
            Live · via {event.source_name}
          </div>
          <h1 className="font-heading text-2xl leading-[1.1] sm:text-4xl md:text-5xl tracking-tight max-w-3xl">
            {event.title}
          </h1>
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] sm:text-sm">
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="size-4 shrink-0" />
              {formatDate(event.start_date)}
            </span>
            {(event.venue || event.city) && (
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <MapPin className="size-4 shrink-0" />
                <span className="truncate">{[event.venue, event.city].filter(Boolean).join(" · ")}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12 grid md:grid-cols-3 gap-8 md:gap-10">
        <div className="md:col-span-2 space-y-6 md:order-1 order-2">
          {event.full_description && (
            <div className="prose prose-neutral max-w-none">
              <p className="whitespace-pre-wrap leading-relaxed text-[15px] sm:text-base">
                {event.full_description}
              </p>
            </div>
          )}
          {event.gallery_images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {event.gallery_images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  loading="lazy"
                  className="aspect-square w-full object-cover rounded-xl"
                />
              ))}
            </div>
          )}
        </div>

        <aside className="space-y-4 md:order-2 order-1">
          {/* Desktop / tablet ticket CTA — mobile uses sticky bar below */}
          {event.ticket_url && (
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center justify-center gap-2 w-full py-3 rounded-full bg-foreground text-background font-semibold hover:opacity-90 transition"
            >
              Get tickets
              <ArrowUpRight className="size-4" />
            </a>
          )}
          <div className="rounded-2xl border border-border/40 bg-card p-4 sm:p-5 grid grid-cols-2 md:grid-cols-1 gap-3 sm:gap-4 text-sm">
            {event.organizer && (
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Organizer</p>
                <p className="truncate">{event.organizer}</p>
              </div>
            )}
            {event.category && (
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Category</p>
                <p className="truncate">{event.category}</p>
              </div>
            )}
            <div className="col-span-2 md:col-span-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Source</p>
              <a
                href={event.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {event.source_name}
              </a>
            </div>
          </div>
        </aside>
      </div>

      {/* MOBILE STICKY TICKET CTA */}
      {event.ticket_url && (
        <div className="md:hidden fixed bottom-20 inset-x-0 z-40 px-4">
          <a
            href={event.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-foreground text-background font-semibold shadow-[0_10px_30px_-10px_rgba(15,17,42,0.5)] active:scale-[0.99] transition"
          >
            Get tickets
            <ArrowUpRight className="size-4" />
          </a>
        </div>
      )}
    </article>
  );
}

